/**
 * This store mainly handles the requests and responses from the highlight-server which is used for Systime Highlights.
 * This is the closest to 'raw' data that you can get, everything else is wrapped in the Highlights Service, which this Vuex Store uses.
 *
 * This store contains the state of the highlight-server, and you can use this to persist and do CRUD operations.
 */

import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';

import { Vue } from 'nuxt-property-decorator';
import HighlightService from '../components/ui/highlight/HighlightService';

import { Site } from '../types/initialData';
import { Page } from '../types/pageContract';
import { NoteHighlight } from '../types/highlightContract';

interface HighlightMap {
  [key: string]: NoteHighlight[];
}

@Module({
  stateFactory: true,
  namespaced: true
})
export default class HighlightsStore extends VuexModule {
  currentHighlights: HighlightMap = Object.create(null);
  currentHighlightId: string = '';

  @Action({ rawError: true, commit: 'setHighlightsForPid' })
  async fetchHighlightsForPage(payload: { pid: string; site: Site }) {
    const { pid, site } = payload;

    const highlights = await HighlightService.getHighlightsForPage(pid, site);

    return { pid, highlights };
  }

  @Action({ rawError: true, commit: 'setAllHighlights' })
  async fetchHighlightsForIsbn(payload: { site: Site }) {
    const { site } = payload;

    const highlights = await HighlightService.getHighlightsForIsbn(site);

    return { highlights };
  }

  @Action({ rawError: true, commit: 'addHighlightForPid' })
  async addHighlightToPage(payload: {
    pid: string;
    site: Site;
    highlight: NoteHighlight;
    page: Page;
  }) {
    const { pid, site, highlight, page } = payload;

    const result = await HighlightService.createHighlightToPage(
      pid,
      site,
      highlight,
      page
    );

    return { pid, highlight: result };
  }

  @Action({ rawError: true, commit: 'removeHighlightForPid' })
  async deleteHighlightFromPage(payload: {
    pid: string;
    site: Site;
    highlight: NoteHighlight;
  }) {
    const { pid, site, highlight } = payload;

    await HighlightService.deleteHighlight(pid, site, highlight);

    return { pid, highlight };
  }

  @Action({ rawError: true, commit: 'updateHighlightForPid' })
  async updateHighlightFromPage(payload: {
    pid: string;
    site: Site;
    highlight: NoteHighlight;
    page: Page;
  }) {
    const { pid, site, highlight, page } = payload;

    const result = await HighlightService.updateHighlightOnPage(
      pid,
      site,
      highlight,
      page
    );

    return { pid, highlight: result };
  }

  @Mutation
  updateCurrentHighlightId(newId: string): void {
    this.currentHighlightId = newId;
  }

  @Mutation
  addHighlightForPid({
    pid,
    highlight
  }: {
    pid: string;
    highlight: NoteHighlight;
  }) {
    if (!this.currentHighlights[pid]) {
      Vue.set(this.currentHighlights, pid, []);
    }
    const result = this.currentHighlights[pid];
    result.push(highlight);
    Vue.set(this.currentHighlights, pid, result);
  }

  @Mutation
  setAllHighlights({ highlights }: { highlights: NoteHighlight[] }) {
    this.currentHighlights = Object.create(null);
    highlights.forEach((note) => {
      if (this.currentHighlights[note.pid]) {
        const current = this.currentHighlights[note.pid];
        current.push(note);
        Vue.set(this.currentHighlights, note.pid, current);
      } else {
        Vue.set(this.currentHighlights, note.pid, [note]);
      }
    });
  }

  @Mutation
  setHighlightsForPid({
    pid,
    highlights
  }: {
    pid: string;
    highlights: NoteHighlight[];
  }) {
    Vue.set(this.currentHighlights, pid, highlights);
  }

  @Mutation
  removeHighlightForPid({
    pid,
    highlight
  }: {
    pid: string;
    highlight: NoteHighlight;
  }) {
    const highlights = this.currentHighlights[pid];
    if (highlights) {
      highlights.splice(highlights.indexOf(highlight), 1);
      Vue.set(this.currentHighlights, pid, highlights);
    }
  }

  @Mutation
  updateHighlightForPid({
    pid,
    highlight
  }: {
    pid: string;
    highlight: NoteHighlight;
  }) {
    const highlights = this.currentHighlights[pid];
    if (highlights) {
      const index = highlights.findIndex((hl) => hl.id === highlight.id);
      if (index !== -1) {
        highlights.splice(index, 1);
        highlights.push(highlight);
        Vue.set(this.currentHighlights, pid, highlights);
      } else {
        throw new Error('Could not find highlight with id: ' + highlight.id);
      }
    }
  }

  @Mutation
  clearHighlights() {
    this.currentHighlights = Object.create(null);
  }

  // Use this to represent all the highlights/notes that are given on a page
  get highlightsForPid(): (pid: string) => NoteHighlight[] | undefined {
    return (pid: string) => {
      if (this.currentHighlights[pid]) {
        return [...this.currentHighlights[pid]];
      } else {
        return this.currentHighlights[pid];
      }
    };
  }

  get highlightsForIsbn(): NoteHighlight[] | undefined {
    return (
      Object.entries(this.currentHighlights)
        ?.map(([_k, hls], _i) => hls)
        ?.reduce((prev, hls) => [...prev, ...hls], []) || []
    );
  }

  // Use this for the local SystimeHighlight that surrounds a CID block
  get highlightsForPidAndCid(): (
    pid: string,
    cid: number
  ) => NoteHighlight[] | undefined {
    return (pid: string, cid: number) =>
      this.highlightsForPid(pid)?.filter(
        (hl) =>
          hl?.recreationData?.startCID === cid ||
          hl?.recreationData?.endCID === cid ||
          // the CID is not relevant (will always be 0).
          // Pagenotes would be filtered and not used anywhere, as all highlightwrappers use a CID.
          // The solution is to let the highlightwrapper with the first occuring CID to take responsibility of the pagenotes and attach them to its header.
          hl?.recreationData?.ibkngData?.isPageNote
      );
  }

  get isHighlightsForPidPopulated(): (pid: string) => boolean {
    return (pid: string) =>
      this.currentHighlights[pid] && this.currentHighlights[pid].length > 0;
  }

  get selectedHighlightId(): string {
    return this.currentHighlightId;
  }
}
